
import React, { useState } from 'react';

// Tooltip component for tag hover
function Tooltip({ text, children }) {
  const [show, setShow] = useState(false);
  return (
    <span className="relative"
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
      tabIndex={0}
      onFocus={() => setShow(true)}
      onBlur={() => setShow(false)}
    >
      {children}
      {show && (
        <span className="absolute z-20 left-1/2 -translate-x-1/2 mt-2 px-2 py-1 bg-black text-white text-xs rounded shadow-lg whitespace-nowrap">
          {text}
        </span>
      )}
    </span>
  );
}

// Modal for image preview
function ImageModal({ src, alt, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60" onClick={onClose}>
      <img src={src} alt={alt} className="max-h-[80vh] max-w-[90vw] rounded shadow-2xl border-4 border-white" />
    </div>
  );
}

export default function QuestionCard({
  q,
  API_HOST,
  token,
  setError,
  onEdit,
  onDelete,
  fetchData
}) {
  const [modalImg, setModalImg] = useState(null);

  const buildImageUrl = (url) => {
    if (!url) return null;
    const trimmed = url.toString().trim();
    if (!trimmed) return null;
    if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
      return trimmed;
    }
    if (trimmed.startsWith('//')) {
      return `https:${trimmed}`;
    }
    if (trimmed.startsWith('/')) {
      return `${API_HOST.replace(/\/+$/, '')}${trimmed}`;
    }
    return `${API_HOST.replace(/\/+$/, '')}/${trimmed}`;
  };

  const questionImages = Array.isArray(q.images) ? q.images : [];

  // Tag rendering with tooltip for truncated text
  const Tag = ({ text, className }) => (
    <Tooltip text={text}>
      <span className={`text-xs font-semibold px-2 py-1 rounded-full border truncate max-w-[120px] ${className}`}>{text}</span>
    </Tooltip>
  );

  return (
    <div
      key={q.id}
      className="group p-4 bg-white rounded-xl border border-slate-200 shadow hover:shadow-2xl hover:-translate-y-1 transition-all duration-200 focus-within:ring-2 focus-within:ring-indigo-300 outline-none"
      tabIndex={0}
      aria-label={`Question ${q.id}: ${q.text}`}
    >
      <div className="flex flex-col md:flex-row md:justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 mb-2">
            <span className="text-xs text-slate-500 select-none">QID: <strong className="text-indigo-700">{q.id}</strong></span>
          </div>
          <p className="font-semibold text-slate-800 text-sm md:text-base line-clamp-3" title={q.text}>{q.text}</p>
          {questionImages.length > 0 && (
            <div className="mt-2 flex gap-2 overflow-x-auto">
              {questionImages.map(img => {
                const imageUrl = buildImageUrl(img.url);
                return imageUrl ? (
                  <img
                    key={img.id}
                    src={imageUrl}
                    alt={`question ${q.id} image`}
                    className="max-h-24 rounded-md object-contain border border-gray-200 cursor-pointer transition hover:scale-105 focus:scale-105"
                    tabIndex={0}
                    onClick={() => setModalImg(imageUrl)}
                    onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') setModalImg(imageUrl); }}
                  />
                ) : null;
              })}
            </div>
          )}
          <div className="mt-2 flex flex-wrap gap-2">
            {q.test_title && <Tag text={q.test_title} className="bg-purple-100 text-purple-800 border-purple-200" />}
            {q.subject_name && <Tag text={q.subject_name} className="bg-indigo-100 text-indigo-800 border-indigo-200" />}
            {q.topic_name && <Tag text={q.topic_name} className="bg-teal-100 text-teal-800 border-teal-200" />}
            {q.context_title && <Tag text={q.context_title} className="bg-gray-100 text-gray-800 border-gray-200" />}
            {q.difficulty && <Tag text={q.difficulty} className="bg-yellow-100 text-yellow-800 border-yellow-200" />}
          </div>
        </div>
        <div className="flex-shrink-0 flex items-center gap-2 self-start md:self-center">
          <button
            className="px-3 py-1 rounded-lg bg-blue-100 text-blue-700 hover:bg-blue-200 focus:bg-blue-300 focus:outline-none transition shadow-sm"
            onClick={() => onEdit(q)}
            aria-label={`Edit question ${q.id}`}
          >
            Edit
          </button>
          <button
            className="px-3 py-1 rounded-lg bg-red-100 text-red-700 hover:bg-red-200 focus:bg-red-300 focus:outline-none transition shadow-sm"
            onClick={() => onDelete(q)}
            aria-label={`Delete question ${q.id}`}
          >
            Delete
          </button>
        </div>
      </div>
      {modalImg && (
        <ImageModal src={modalImg} alt="Question image preview" onClose={() => setModalImg(null)} />
      )}
    </div>
  );
}
